
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useEffect } from 'react';
const Steps = dynamic(() => import('@/components/atoms/Steps'));
const CallToAction = dynamic(() => import('@/components/CallToAction'));
const Footer = dynamic(() => import('@/components/layout/Footer'));
const OtherFeatures = dynamic(() => import('@/components/OtherFeatures'));
const PublicCaption = dynamic(() => import('@/components/PublicCaption'));
const Seo = dynamic(() => import('@/components/Seo'));
const TemplatesHeroSection = dynamic(() => import('@/components/TemplatesHeroSection'));
const TemplateUseCases = dynamic(() => import('@/components/TemplateUseCases'));
// load Testimonials component only on client side
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const FaqTemplate = dynamic(() => import('@/components/FaqTemplate'));
const PresetRecommendations = dynamic(() => import('@/components/PresetRecommendations'));
const SocialStoriesBotPromo = dynamic(() => import('@/components/SocialStoriesBotPromo'));
const SocialStoryExample = dynamic(() => import('@/components/SocialStoryExample'));
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { getPublicCompletions } from '@/data/completions';
import { getCompletionsFromPublicGenerations } from '@/data/public_completions';
import { HAS_FREE_PLAN } from '@/constants/common';
import { CLINICAL_PRESET_SLUGS, Preset, PresetCategory, presets, } from '@/constants/presets';
// These two corpora total ~1MB of string literals. They are referenced ONLY
// inside getStaticProps (Next.js strips them from the client bundle) — never
// pass them to the client or import them from client components.
import { templateFaqs } from '@/constants/templateFaqs';
import { templateUseCases } from '@/constants/templateUseCases';
import { useUser } from '@/utils/useUser';
// import Banner from '@/components/atoms/Banner'; // Black Friday is over
interface TemplateProps {
    completions: Array<{
        id: number;
        completion: string | null;
        request_body?: {
            keywords?: string;
            [key: string]: any;
        } | null;
        preset: string | null;
        user_prompt: string | null;
    }>;
    preset: Preset;
    locale?: string;
    otherPresets: Preset[];
    useCases?: {
        title: string;
        description: string;
    }[];
    faqs?: {
        question: string;
        answer: string;
    }[];
    isClinical?: boolean;
}
const Template = ({ completions = [], preset, locale = 'en', otherPresets, useCases = [], faqs = [], isClinical = false, }: TemplateProps) => {
    const router = useRouter();
    // const { slug } = router.query;
    const { t, lang } = useLocale('tools');
    const { user } = useUser();
    // const preset = presets.find((preset) => preset.slug === slug);
    useEffect(() => {
        if (!preset)
            return;
        const storedPresets = JSON.parse(localStorage.getItem('favourites') || '[]');
        if (!storedPresets.includes(preset.slug)) {
            localStorage.setItem('favourites', JSON.stringify([...storedPresets, preset.slug]));
        }
        if (preset.isAbsoluteURL) {
            //redirect to the landing page
            router.push(preset.landingPageSlug as string);
        }
    }, [preset]);
    if (!preset)
        return null;
    if (user) {
        router.push(`/presets/${preset.slug}`, `/presets/${preset.slug}`, {
            locale: lang,
        });
    }
    return (<main className='bg-gray-100 dark:bg-slate-900'>
      {/* <Banner /> Black Friday is over */}
      <Seo templateTitle={lang === 'en'
            ? `${preset.isFree ? 'Free ' : ''}${preset.title.includes('AI') ? '' : 'AI '}${t(preset.title)}`
            : t(preset.title)} description={lang === 'en'
            ? `${t(preset.description)}${preset.isFree ? ' Use it for Free.' : ''}`
            : t(preset.description)} image={`https://easy-peasy.ai/api/og-template?title=${t(preset.title)}&description=${t(preset.description)}`}/>
      <div className='relative overflow-hidden bg-white dark:bg-slate-900'>
        <div className='mx-auto'>
          <div className='relative z-10 bg-white dark:bg-slate-900  lg:w-full '>
            <TemplatesHeroSection preset={preset}/>
          </div>
        </div>
      </div>
      <div className='bg-gray-100 px-5 py-20 dark:bg-slate-900'>
        <div className='mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8'>
          <span className='text-lg font-semibold text-indigo-600'>
            {t('Save hours of your time')}
          </span>
          <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('Harness AI to dramatically speed up your content creation')}
          </p>
          <p className='mx-auto mb-5 mt-5 max-w-prose text-xl text-gray-500 dark:text-slate-400'>
            {t(`Our AI-powered tool can generate high-quality, tailored content in seconds, not hours. Boost your productivity and focus on what really matters.`)}
          </p>
          {/* <TemplateForm preset={preset} /> */}
        </div>
      </div>

      {/* Example story first — visitors want to see what they'll get before
            the bot promo and how-to sections. */}
      {lang === 'en' && preset.slug === 'social-stories-generator' && (<SocialStoryExample />)}

      {lang === 'en' && preset.slug === 'social-stories-generator' && (<SocialStoriesBotPromo />)}

      {lang === 'en' && <TemplateUseCases useCases={useCases}/>}

      <section className='bg-white dark:bg-slate-800'>
        <div className='mx-auto max-w-md px-4 py-5 text-center sm:max-w-3xl sm:px-6 sm:py-10 lg:max-w-7xl lg:px-8'>
          <h2 className='mt-2 text-3xl font-bold tracking-tight text-primary-600 sm:text-4xl'>
            {lang === 'en'
            ? `How to use the ${preset.title}`
            : t(`It's easy to get started`)}
          </h2>
          {/* <p className='mx-auto mt-5 max-w-prose text-xl text-gray-500'>
          With AI's help, you can create copy that is perfectly tailored to
          your audience and your message, so you can stay ahead of the
          competition.
        </p> */}
        </div>
        <div className='flex justify-center px-5 pt-10'>
          <Steps preset={preset}/>
        </div>
      </section>

      {lang === 'en' && preset.slug === 'social-stories-generator' ? null : (<div className='relative mx-auto max-w-4xl md:px-8 xl:px-0'>
          <div className='pb-16 pt-10'>
            <div className='px-4 sm:px-6 md:px-0'>
              <div className='mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8'>
                {lang === 'en' ? (<h2 className='mb-2text-3xl text-h2 primary-600 mt-2 font-bold tracking-tight sm:text-4xl'>
                    {t(preset?.title).replace('Generator', '')} Templates &
                    Examples
                  </h2>) : (<h2 className='mb-2text-3xl text-h2 primary-600 mt-2 font-bold tracking-tight sm:text-4xl'>
                    {t('Generated text by AI')}
                  </h2>)}
              </div>
              <div className='mt-5'>
                <ul className='space-y-4'>
                  {completions
                .filter((completion): completion is typeof completion & {
                completion: string;
            } => completion.completion !== null &&
                typeof completion.completion === 'string')
                .map((completion) => (<PublicCaption key={completion.id} caption={{
                    id: Number(completion.id),
                    text: completion.completion,
                    preset: completion.preset || undefined,
                }} completion={{
                    id: Number(completion.id),
                    completion: completion.completion,
                    preset: completion.preset || '',
                    user_prompt: completion.user_prompt || '',
                    request_body: completion.request_body,
                    prompt: '',
                    user_id: '',
                    response_data: {
                        usage: {
                            prompt_tokens: 0,
                            completion_tokens: 0,
                            total_tokens: 0,
                        },
                    },
                    request: {
                        model: '',
                        messages: [],
                        temperature: 0,
                        max_tokens: 0,
                    },
                }}/>))}
                </ul>
              </div>
            </div>
          </div>
          <div className='mb-16 sm:flex sm:justify-center'>
            <div className='rounded-md shadow'>
              <Link href={HAS_FREE_PLAN
                ? `/login?preset=${encodeURIComponent(preset.loginPreset || preset.slug)}#auth-sign-up`
                : '/#pricing'} className='flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700 md:px-10 md:py-4 md:text-lg'>
                {preset.onlyForPaidPlans
                ? t('Get started')
                : t('Try it yourself for free')}
              </Link>
            </div>
          </div>
        </div>)}
      {lang === 'en' && <FaqTemplate faqs={faqs} isClinical={isClinical}/>}
      <OtherFeatures presets={otherPresets}/>
      <Testimonials />
      {/* <ContactUs /> */}
      <CallToAction preset={preset.loginPreset || preset.slug}/>
      <Footer />
    </main>);
};
export default Template;
export async function getStaticPaths() {
    // Presets flagged `isAbsoluteURL` live on their own landing page
    // (`landingPageSlug`) and only render a client-side redirect here, so there
    // is nothing worth prerendering for them. `fallback: true` still serves the
    // redirect on demand for any inbound link.
    const landingPresets = presets.filter((preset) => !preset.hideLandingPage && !preset.isAbsoluteURL);
    const locales = ['en', 'ja', 'es', 'fr', 'it', 'ko', 'zh', 'de'];
    return {
        paths: locales.flatMap((locale) => landingPresets.map((preset) => ({
            params: { slug: `${preset.slug}` },
            locale,
        }))),
        fallback: true,
    };
}
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const locale = ctx.locale as string;
    // map es to Spanish, ja to Japanese
    let lang;
    switch (locale) {
        case 'es':
            lang = 'Spanish';
            break;
        case 'ja':
            lang = 'Japanese';
            break;
        case 'fr':
            lang = 'French';
            break;
        case 'it':
            lang = 'Italian';
            break;
        case 'de':
            lang = 'German';
            break;
        case 'ko':
            lang = 'Korean';
            break;
        case 'zh':
            lang = 'Chinese';
            break;
        case 'th':
            lang = 'Thai';
            break;
        case 'ar':
            lang = 'Arabic';
            break;
        case 'pt':
            lang = 'Portuguese';
            break;
        case 'ms':
            lang = 'Malay';
            break;
        default:
            lang = '';
    }
    console.log('lang', lang, locale);
    let completions: Array<{
        id: number;
        completion: string | null;
        request_body?: {
            keywords?: string;
            [key: string]: any;
        } | null;
        preset: string | null;
        user_prompt: string | null;
    }> = await getPublicCompletions(ctx?.params?.slug as string, lang);
    if (completions.length === 0) {
        completions = await getCompletionsFromPublicGenerations(ctx?.params?.slug as string, lang);
    }
    const preset = presets.find((preset) => preset.slug === (ctx?.params?.slug as string));
    if (!preset) {
        return { notFound: true };
    }
    // do not include icon from preset, exclude it from the preset
    const { icon, ...presetWithoutIcon } = preset;
    const templateTitleWords = presetWithoutIcon.title
        .replace('Generator', '')
        .trim()
        .split(' ');
    const otherPresets = presets
        .filter((p) => (p.category === preset.category ||
        p.category.includes(p.category[0] as PresetCategory)) &&
        p.slug !== preset.slug &&
        !p.hideLandingPage &&
        !p.isAbsoluteURL &&
        p.slug !== 'dynamic')
        .map((preset) => {
        const { icon, ...presetWithoutIcon } = preset;
        return presetWithoutIcon;
    });
    const presetsWithTitleMatch = otherPresets
        .filter((preset) => templateTitleWords.some((word) => preset.title.includes(word)))
        .slice(0, 12);
    const remainingPresets = otherPresets
        .filter((preset) => !presetsWithTitleMatch.includes(preset))
        .slice(0, 12 - presetsWithTitleMatch.length);
    const finalPresets = [...presetsWithTitleMatch, ...remainingPresets];
    const slug = ctx?.params?.slug as string;
    return {
        props: {
            completions,
            preset: presetWithoutIcon,
            locale,
            otherPresets: finalPresets,
            // Only the current template's content ships to the client (a few KB in
            // __NEXT_DATA__) instead of the full ~1MB corpora in the JS chunk.
            useCases: templateUseCases[slug] || [],
            faqs: templateFaqs[slug] || [],
            isClinical: CLINICAL_PRESET_SLUGS.includes(slug),
        },
    };
};

    async function __Next_Translate__getStaticProps__1a0cbfb65fd__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/templates/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb65fd__ as getStaticProps }
  